import { Injectable, inject } from '@angular/core';
import { NavigationItem } from '@lyceo/homi-dashboard/shared/util-models';
import { AuthActions, AuthFacade } from '@lyceo/shared/auth/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, of, switchMap } from 'rxjs';
import { DashboardNavigationService } from '../../dashboard-navigation.service';
import {
  UserActions,
  loadNavigationActions,
  WalkthroughModalActions,
} from './user.actions';
import { isDefined } from '@lyceo/shared/util';
import {
  PlanModuleActions,
  PlanPageActions,
} from '@lyceo/shared/plan/data-access';
import { HomiSettingsActions } from '@lyceo/shared/homi-settings/data-access';
import { PupilContext } from '@lyceo/shared/plan/util-models';
import { ModalController } from '@ionic/angular/standalone';
import { IonicComponentRef } from '@lyceo/shared/util-models';
import {
  instructorPlanImages,
  WALKTHROUGH_MODAL,
} from '@lyceo/shared/walkthrough/util';
import { UserFacade } from './user.facade';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly navService = inject(DashboardNavigationService);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly walkthroughModal =
    inject<IonicComponentRef>(WALKTHROUGH_MODAL);
  private readonly userFacade: UserFacade = inject(UserFacade);

  public initMenuItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loadUserProfileSuccess),
      map(() => this.authFacade.userId()),
      filter(isDefined),
      switchMap(() =>
        this.navService.getNavigationItems().pipe(
          map((result: NavigationItem[]) =>
            loadNavigationActions.loadNavigationItemsSuccess({
              navigationItems: result,
            }),
          ),
          catchError((error) =>
            of(loadNavigationActions.loadNavigationItemsFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getHomiSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanModuleActions.pupilContextChanged),
      filter((action) => isDefined(action.pupilContext)),
      map((action) =>
        HomiSettingsActions.getSettingsByPupilId({
          pupilId: (action.pupilContext as PupilContext).pupilId,
        }),
      ),
    );
  });

  public showWalkthroughModal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.walkthroughClicked, PlanPageActions.init),
      filter(() => this.userFacade.showWalkthrough()),
      exhaustMap(async () => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.walkthroughModal,
          componentProps: {
            walkthroughImages: instructorPlanImages,
            walkthroughTitle: 'Walkthrough',
          },
          cssClass: ['walkthrough-modal'],
          backdropDismiss: false,
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map(() => WalkthroughModalActions.modalClosed()),
    );
  });
}
